import { useEffect, useState } from "react";
import { useGetIdentity, useNotify, useRedirect } from "ra-core";
import { AlertTriangle, Plus, Search, Edit2, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getSupabaseClient } from "../providers/supabase/supabase";

type Complaint = {
  id: number;
  order_id: number | null;
  contact_id: number | null;
  complaint_type: string;
  claim_amount: number | null;
  currency: string;
  responsible_party: string;
  status: string;
  resolution: string | null;
  compensation_amount: number | null;
  description: string | null;
  notes: string | null;
  created_at: string;
  resolved_at: string | null;
  orders?: { order_number: string } | null;
  contacts?: { first_name: string; last_name: string } | null;
};

type Order = { id: number; order_number: string };

const STATUS_BADGE: Record<string, string> = {
  open:          "bg-red-100 text-red-700",
  investigating: "bg-yellow-100 text-yellow-700",
  resolved:      "bg-blue-100 text-blue-700",
  closed:        "bg-gray-100 text-gray-500",
};

const TYPE_LABELS: Record<string, string> = {
  quality:   "Quality",
  shortage:  "Shortage",
  packaging: "Packaging",
  delay:     "Delay",
  document:  "Document",
  other:     "Other",
};

const RESOLUTION_LABELS: Record<string, string> = {
  reship:     "Reship",
  refund:     "Refund",
  discount:   "Discount on Next Order",
  credit:     "Credit Note",
  no_action:  "No Action Required",
};

const emptyForm = () => ({
  order_id: "",
  complaint_type: "quality",
  claim_amount: "",
  currency: "USD",
  responsible_party: "factory",
  status: "open",
  resolution: "",
  compensation_amount: "",
  description: "",
  notes: "",
});

const ComplaintsPage = () => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const redirect = useRedirect();
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Complaint | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = () => {
    const supabase = getSupabaseClient();
    Promise.all([
      supabase
        .from("complaints")
        .select("*, orders(order_number), contacts(first_name, last_name)")
        .order("created_at", { ascending: false }),
      supabase.from("orders").select("id, order_number").order("created_at", { ascending: false }).limit(200),
    ]).then(([cRes, oRes]) => {
      setComplaints(cRes.data as Complaint[] ?? []);
      setOrders(oRes.data ?? []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (c: Complaint) => {
    setEditing(c);
    setForm({
      order_id: c.order_id?.toString() ?? "",
      complaint_type: c.complaint_type,
      claim_amount: c.claim_amount?.toString() ?? "",
      currency: c.currency,
      responsible_party: c.responsible_party,
      status: c.status,
      resolution: c.resolution ?? "",
      compensation_amount: c.compensation_amount?.toString() ?? "",
      description: c.description ?? "",
      notes: c.notes ?? "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.description?.trim()) { notify("Description is required", { type: "error" }); return; }
    setSaving(true);
    const supabase = getSupabaseClient();
    const payload = {
      order_id: form.order_id ? parseInt(form.order_id) : null,
      complaint_type: form.complaint_type,
      claim_amount: form.claim_amount ? parseFloat(form.claim_amount) : null,
      currency: form.currency,
      responsible_party: form.responsible_party,
      status: form.status,
      resolution: form.resolution || null,
      compensation_amount: form.compensation_amount ? parseFloat(form.compensation_amount) : null,
      description: form.description,
      notes: form.notes || null,
      resolved_at: (form.status === "resolved" || form.status === "closed") ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    };
    if (editing) {
      const { error } = await supabase.from("complaints").update(payload).eq("id", editing.id);
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("Complaint updated", { type: "info" });
    } else {
      const { error } = await supabase.from("complaints").insert({ ...payload, created_by: identity?.id ?? null });
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("Complaint logged", { type: "info" });
    }
    setSaving(false); setShowForm(false); load();
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this complaint?")) return;
    await getSupabaseClient().from("complaints").delete().eq("id", id);
    notify("Deleted", { type: "info" }); load();
  };

  const filtered = complaints.filter(c => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (c.orders?.order_number ?? "").toLowerCase().includes(q) ||
        (c.description ?? "").toLowerCase().includes(q) ||
        TYPE_LABELS[c.complaint_type]?.toLowerCase().includes(q);
    }
    return true;
  });

  const openCount = complaints.filter(c => c.status === "open" || c.status === "investigating").length;
  const totalClaim = complaints.filter(c => c.currency === "USD" && c.claim_amount).reduce((a, b) => a + (b.claim_amount ?? 0), 0);

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><AlertTriangle className="w-6 h-6" /> Complaints & Claims</h1>
        <Button size="sm" onClick={openCreate} className="gap-1"><Plus className="w-3.5 h-3.5" /> Log Complaint</Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-card border rounded-lg p-4">
          <div className="text-xs text-muted-foreground mb-1">Open Cases</div>
          <div className="text-xl font-bold text-red-600">{openCount}</div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <div className="text-xs text-muted-foreground mb-1">Total Claims (USD)</div>
          <div className="text-xl font-bold tabular-nums">${totalClaim.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
          <Input className="pl-8 h-8 text-sm" placeholder="Order#, type, description..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex border rounded-lg overflow-hidden">
          {["all", "open", "investigating", "resolved", "closed"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No complaints found.</p>
        </div>
      ) : (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground uppercase">
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Order</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Description</th>
                <th className="text-right px-4 py-2.5 hidden lg:table-cell">Claim</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Responsible</th>
                <th className="text-center px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="border-t hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    {c.orders?.order_number ? (
                      <button onClick={() => redirect("show", "orders", c.order_id!)}
                        className="text-primary hover:underline flex items-center gap-1 text-xs font-mono">
                        {c.orders.order_number} <ExternalLink className="w-3 h-3" />
                      </button>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3">{TYPE_LABELS[c.complaint_type] ?? c.complaint_type}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground truncate max-w-[200px]">{c.description ?? "—"}</td>
                  <td className="px-4 py-3 text-right hidden lg:table-cell tabular-nums">
                    {c.claim_amount != null ? `${c.currency} ${c.claim_amount.toLocaleString()}` : "—"}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground capitalize">{c.responsible_party.replace("_", " ")}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_BADGE[c.status] ?? ""}`}>{c.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => openEdit(c)} className="p-1 text-muted-foreground hover:text-foreground"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(c.id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={o => !o && setShowForm(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Complaint" : "Log Complaint / Claim"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Linked Order</Label>
              <Select value={form.order_id} onValueChange={v => set("order_id", v)}>
                <SelectTrigger className="h-8"><SelectValue placeholder="Select order..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {orders.map(o => <SelectItem key={o.id} value={o.id.toString()}>{o.order_number}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.complaint_type} onValueChange={v => set("complaint_type", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Responsible Party</Label>
              <Select value={form.responsible_party} onValueChange={v => set("responsible_party", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="factory">Factory</SelectItem>
                  <SelectItem value="forwarder">Freight Forwarder</SelectItem>
                  <SelectItem value="us">Our Side</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Claim Amount</Label>
              <Input className="h-8 text-sm" type="number" step="0.01" placeholder="0.00" value={form.claim_amount} onChange={e => set("claim_amount", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={v => set("currency", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{["USD","EUR","GBP","CNY"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Resolution</Label>
              <Select value={form.resolution} onValueChange={v => set("resolution", v)}>
                <SelectTrigger className="h-8"><SelectValue placeholder="Select resolution..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Pending</SelectItem>
                  {Object.entries(RESOLUTION_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.resolution === "refund" || form.resolution === "credit" || form.resolution === "discount" ? (
              <div className="space-y-1.5">
                <Label>Compensation Amount</Label>
                <Input className="h-8 text-sm" type="number" step="0.01" value={form.compensation_amount} onChange={e => set("compensation_amount", e.target.value)} />
              </div>
            ) : <div />}
            <div className="col-span-2 space-y-1.5">
              <Label>Description *</Label>
              <Textarea className="text-sm resize-none" rows={2} placeholder="Describe the issue..." value={form.description} onChange={e => set("description", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Internal Notes</Label>
              <Textarea className="text-sm resize-none" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : (editing ? "Update" : "Submit")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComplaintsPage;
